import type { DraftTask, Task, TaskDetail } from '@frontend/api-types';
import type { SetOptional } from '@frontend/duck-tape';
import {
  EArray,
  P,
  dateFormatters,
  dayjs,
  isDateWithinDays,
  match,
  pipe,
  reduce,
  sortByDate,
} from '@frontend/duck-tape';
import { TASK_STATUSES } from '../constants';

export const CREATE_TASK_NEXT_STEPS_OPTIONS = [
  {
    description: "We'll show you options, and you make the final decision",
    title: 'Provide options',
    value: 'Start with some options',
  },
  {
    description: "We'll do our best to decide the best option",
    title: 'Just handle it',
    value: 'Go ahead and complete the task',
  },
] as const;

export type CreateTaskNextSteps = (typeof CREATE_TASK_NEXT_STEPS_OPTIONS)[number]['value'];

export type TaskFilterType =
  | 'closedIncomplete'
  | 'done'
  | 'draft'
  | 'inProgress'
  | 'repeating'
  | 'snoozed'
  | 'startingSoon';

export const taskFilterEnum = {
  closedIncomplete: 'closedIncomplete',
  done: 'done',
  draft: 'draft',
  inProgress: 'inProgress',
  repeating: 'repeating',
  snoozed: 'snoozed',
  startingSoon: 'startingSoon',
} as const satisfies { [K in TaskFilterType]: K };

const NON_ACTIONABLE_STATUSES = [...TASK_STATUSES.completed, 'snoozed'] as const;

export const countActionableUnreadTasks = (
  tasks: Pick<Task, 'isUnread' | 'status'>[],
  nonActionableStatuses: readonly string[] = NON_ACTIONABLE_STATUSES,
): number => tasks.filter((t) => t.isUnread && !nonActionableStatuses.includes(t.status)).length;

const isTaskCompletedAtAll = (task: Pick<Task, 'status'>) => TASK_STATUSES.completed.includes(task.status);

/** Zendesk will automatically transition a task to `archived` after 28 days of being closed */
export const canMemberSendMessageInTaskChat = (task: Pick<Task, 'status'>) => task.status !== 'archived';

/** Waiting for a member to send a message to the task chat */
export const isTaskAwaitingMemberResponse = (task: Pick<Task, 'status'>) =>
  TASK_STATUSES.waitingOnYou.includes(task.status);

/** Work as actually been started on this task and it's not closed yet */
export const isTaskInProgress = (task: Pick<Task, 'shotClockStartedAt' | 'status'>): boolean =>
  Boolean(task.shotClockStartedAt && TASK_STATUSES.inProgress.includes(task.status) && task.status !== 'snoozed');

export const isTaskSnoozed = (task: Pick<Task, 'status'>): boolean => task.status === 'snoozed';

export const willTaskReceiveAutomaticRefundOnClose = (
  task: Pick<Task, 'creditCost' | 'shotClockStartedAt' | 'status'>,
) => !isTaskInProgress(task) && task.creditCost > 0;

export const isTaskMarkedAsSuccessfullyCompleted = (
  task: Pick<Task, 'createdAt' | 'memberConfirmationStatus' | 'status'>,
  markAsDoneDeployDate: MaybeNull<DateISOString>,
) => {
  // We're treating all tasks with status "solved" or "archived" that were created before this date as "successfully completed"
  if (!markAsDoneDeployDate || (markAsDoneDeployDate && dayjs(task.createdAt).isBefore(markAsDoneDeployDate))) {
    return isTaskCompletedAtAll(task);
  }
  return isTaskCompletedAtAll(task) && task.memberConfirmationStatus === 'done';
};

export const isTaskMarkedAsFailedToComplete = (task: Pick<Task, 'createdAt' | 'memberConfirmationStatus' | 'status'>) =>
  isTaskCompletedAtAll(task) && task.memberConfirmationStatus === 'not_done';

const isTaskSuccessfullyCompletedLast24Hours = (
  task: Pick<
    Task,
    'createdAt' | 'maxExecutionHrs' | 'memberConfirmationStatus' | 'shotClockStartedAt' | 'startSlotTo' | 'status'
  >,
  markAsDoneDeployDate: MaybeNull<DateISOString>,
): boolean =>
  Boolean(
    isTaskMarkedAsSuccessfullyCompleted(task, markAsDoneDeployDate) &&
      task.shotClockStartedAt &&
      // Task was at least *supposed* to be done in 24 hours
      dayjs().diff(dayjs(task.startSlotTo).add(task.maxExecutionHrs, 'hour'), 'hour') <= 24,
  );

// ** Task is presumably new, we haven't determined when to start this task yet */
const isTaskNoStartTime = (
  task: Pick<Task, 'shotClockStartedAt' | 'startSlotFrom' | 'startSlotTo' | 'status'>,
): boolean => Boolean((task.status === 'new' || task.status === 'open') && !task.startSlotTo);

// ** Task is presumably new, no task session has been started on this yet but we know when it's supposed to start */
export const isTaskStartingSoon = (task: Pick<Task, 'shotClockStartedAt' | 'startSlotTo' | 'status'>): boolean =>
  Boolean((task.status === 'new' || task.status === 'open') && !task.shotClockStartedAt && task.startSlotTo);

// ** Task is an instance of a recurrence */
export const isTaskRepeating = (task: Pick<Task, 'recurrenceDate'>): boolean => Boolean(task.recurrenceDate);

export const isTaskCloseableByMember = (task: Pick<Task, 'status'>) => !isTaskCompletedAtAll(task);

export const isTaskReviewable = (task: Pick<Task, 'isReviewSubmitted'>) => !task.isReviewSubmitted;

type SimplifiedTaskStatus =
  | 'doneLast24Hours'
  | 'doneOver24Hours'
  | 'failedToComplete'
  | 'indeterminate'
  | 'inProgress'
  | 'noStartTime'
  | 'startingSoon';

export const getSimplifiedTaskStatus = (
  task: Pick<
    Task,
    | 'createdAt'
    | 'isStarted'
    | 'maxExecutionHrs'
    | 'memberConfirmationStatus'
    | 'shotClockStartedAt'
    | 'startSlotFrom'
    | 'startSlotTo'
    | 'status'
  >,
  markAsDoneDeployDate: MaybeNull<DateISOString>,
): SimplifiedTaskStatus => {
  // The order in which these checks happen are important
  // Check if work has actually started first, regardless of scheduling
  if (isTaskInProgress(task)) return 'inProgress';
  if (isTaskNoStartTime(task)) return 'noStartTime';
  if (isTaskStartingSoon(task)) return 'startingSoon';
  if (isTaskSuccessfullyCompletedLast24Hours(task, markAsDoneDeployDate)) return 'doneLast24Hours';
  if (isTaskMarkedAsSuccessfullyCompleted(task, markAsDoneDeployDate)) return 'doneOver24Hours';
  if (isTaskMarkedAsFailedToComplete(task)) return 'failedToComplete';
  return 'indeterminate';
};

export const groupTasksBySimplifiedStatus = (
  tasks: Task[],
  markAsDoneDeployDate: MaybeNull<DateISOString>,
): Record<SimplifiedTaskStatus, Task[]> =>
  reduce(
    tasks,
    (acc, task): Record<SimplifiedTaskStatus, Task[]> => {
      const status = getSimplifiedTaskStatus(task, markAsDoneDeployDate);
      return { ...acc, [status]: [...(acc[status] || []), task] };
    },
    {
      canceled: [],
      doneLast24Hours: [],
      doneOver24Hours: [],
      failedToComplete: [],
      inProgress: [],
      indeterminate: [],
      noStartTime: [],
      startingSoon: [],
    } as Record<SimplifiedTaskStatus, Task[]>,
  );

export const getTaskSubtitle = (
  task: Pick<
    Task,
    | 'createdAt'
    | 'isStarted'
    | 'maxExecutionHrs'
    | 'memberConfirmationStatus'
    | 'shotClockStartedAt'
    | 'startSlotFrom'
    | 'startSlotTo'
    | 'status'
  >,
  markAsDoneDeployDate: MaybeNull<DateISOString>,
  nextTriggerAt?: DateTimeISOString,
) => {
  if (isTaskSnoozed(task) && nextTriggerAt) {
    const wakeDate = dayjs(nextTriggerAt);
    const today = dayjs().startOf('day');
    const tomorrow = today.add(1, 'day');

    if (wakeDate.isSame(today, 'day')) {
      return 'Wakes up today';
    } else if (wakeDate.isSame(tomorrow, 'day')) {
      return 'Wakes up tomorrow';
    }
    return `Wakes up ${dateFormatters['MMM D'](wakeDate)}`;
  }

  const simplifiedStatus = getSimplifiedTaskStatus(task, markAsDoneDeployDate);

  const isTaskInRoutingWindow =
    task.startSlotFrom &&
    task.startSlotTo &&
    dayjs(task.startSlotFrom).isBefore(dayjs()) &&
    dayjs().isBetween(dayjs(task.startSlotFrom), dayjs(task.startSlotTo), null, '[)');

  return (
    match(simplifiedStatus)
      // Note: Since we're not sure about the product direction in terms of the "snoozed" status, we're just treating it
      // as an "active" task for now
      .with('inProgress', () => (isTaskAwaitingMemberResponse(task) ? 'Ready to review' : 'Active'))
      .with('startingSoon', () => {
        if (isTaskInRoutingWindow) return 'Routing to copilot';
        const startTime = dayjs(task.startSlotFrom);
        const startTimeTo = dayjs(task.startSlotTo);
        const today = dayjs().startOf('day');
        const tomorrow = today.add(1, 'day');

        if (startTimeTo.isBefore(today) && !today.isBetween(startTime, startTimeTo, null, '[]')) return 'Starting soon';

        if (startTime.isSame(today, 'day')) {
          return `Starts at ${dateFormatters['h:mm A'](startTime)}`;
        } else if (startTime.isSame(tomorrow, 'day')) {
          return `Starts tomorrow at ${dateFormatters['h:mm A'](startTime)}`;
        } else {
          return `Starts ${dateFormatters['MMM D'](startTime)} at ${dateFormatters['h:mm A'](startTime)}`;
        }
      })
      .with(P.union('doneLast24Hours', 'doneOver24Hours'), () => 'Done')
      .with('failedToComplete', () => 'Closed')
      .with('noStartTime', () => '')
      .with('indeterminate', () => '')
      .exhaustive()
  );
};

export const getTaskStartText = (task: Pick<TaskDetail, 'startSlotFrom' | 'startSlotTo'>): string => {
  if (!task.startSlotFrom || !task.startSlotTo) return '';
  return `${dateFormatters['ddd, MMM D'](task.startSlotFrom)} (${dateFormatters['ha'](task.startSlotFrom)} - ${dateFormatters['ha'](task.startSlotTo)})`;
};

export type TaskLikeObject =
  | (DraftTask & { __typename: 'DraftTask' })
  // | (Recurrence & { __typename: 'Recurrence' })
  | (SetOptional<Task, 'zendeskId'> & { __typename: 'Task' });

export const addTaskTypename = (object: SetOptional<Task, 'zendeskId'>) =>
  ({
    ...object,
    __typename: 'Task',
  }) satisfies TaskLikeObject;

export const addDraftTypename = (object: DraftTask) =>
  ({
    ...object,
    __typename: 'DraftTask',
  }) satisfies TaskLikeObject;

export const sortTasksByUnreadAndDate = <T extends TaskLikeObject>(tasks: T[]): T[] =>
  EArray.sort(tasks, (a: T, b: T) => {
    const aIsUnread = a.__typename === 'Task' && a.isUnread;
    const bIsUnread = b.__typename === 'Task' && b.isUnread;

    if (aIsUnread && !bIsUnread) return -1;
    if (!aIsUnread && bIsUnread) return 1;

    const timeDiff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    return timeDiff > 0 ? 1 : timeDiff < 0 ? -1 : 0;
  });

export const sortTasksByOpenAndDate = <T extends TaskLikeObject>(tasks: T[]): T[] =>
  EArray.sort(tasks, (a: T, b: T) => {
    const aIsOpen = a.__typename === 'Task' && TASK_STATUSES.inProgress.includes(a.status);
    const bIsOpen = b.__typename === 'Task' && TASK_STATUSES.inProgress.includes(b.status);

    if (aIsOpen && !bIsOpen) return -1;
    if (!aIsOpen && bIsOpen) return 1;

    const timeDiff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    return timeDiff > 0 ? 1 : timeDiff < 0 ? -1 : 0;
  });

export const getLatestTasksForDisplay = <T extends TaskLikeObject>(tasks: T[], minCount: number = 5): T[] => {
  const sortedTasks = sortTasksByUnreadAndDate(tasks);
  const unreadCount = sortedTasks.filter((t) => t.__typename === 'Task' && t.isUnread).length;
  return sortedTasks.slice(0, Math.max(minCount, unreadCount));
};

type HomePageTaskSections = {
  doneLast24HoursTasks: ReturnType<typeof addTaskTypename>[];
  inProgressTasks: ReturnType<typeof addTaskTypename>[];
  last7daysDraftTasks: ReturnType<typeof addDraftTypename>[];
  latestTasks: ReturnType<typeof addTaskTypename>[];
  startingSoonTasks: ReturnType<typeof addTaskTypename>[];
};

const prepareTaskListForHome = (t: Task[]) =>
  pipe(t, sortByDate({ getter: (t) => t.updatedAt, sortDirection: 'desc' }), EArray.map(addTaskTypename));

export const getHomePageTaskSections = ({
  draftTasks,
  markAsDoneDeployDate,
  tasks,
}: {
  draftTasks: DraftTask[];
  markAsDoneDeployDate: MaybeNull<DateISOString>;
  tasks: Task[];
}): HomePageTaskSections => {
  const last7daysDraftTasks = pipe(
    draftTasks,
    EArray.filter((task) => isDateWithinDays(task.updatedAt, 7)),
    sortByDate({ getter: (t) => t.updatedAt, sortDirection: 'desc' }),
    EArray.map(addDraftTypename),
  );

  const latestTasks = pipe(
    tasks,
    EArray.filter((task) => !TASK_STATUSES.completed.includes(task.status) && task.status !== 'snoozed'),
    prepareTaskListForHome,
  );

  const { doneLast24Hours, inProgress, startingSoon } = groupTasksBySimplifiedStatus(tasks, markAsDoneDeployDate);

  return {
    doneLast24HoursTasks: prepareTaskListForHome(doneLast24Hours),
    inProgressTasks: prepareTaskListForHome(inProgress),
    last7daysDraftTasks,
    latestTasks,
    startingSoonTasks: prepareTaskListForHome(startingSoon),
  };
};
